// Read-only Arive panels for a loan file (ticket 134 P1): status & key dates,
// trackers, pricing & lock, borrowers, loan team, and the gated company-money
// panel.
// The money panel shows exactly when the response carries the money keys; the
// server strips them for users without the permission, so no role check here.
import React from 'react';
import { ModuleHeader } from '../shared/formFields';
import AriveBadge from '../../board/AriveBadge';
import { AriveChips } from '../shared/AriveChips';
import { ariveDeepLink } from '../../ariveManaged';
import { formatMoney } from '../../shared/money';
import { formatDate, formatDateTime } from '../../kit/time';

const titleCase = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const has = (obj, key) => !!obj && Object.prototype.hasOwnProperty.call(obj, key);
const present = (v) => v !== null && v !== undefined && v !== '';

function Panel({ title, testId, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid={testId}>
      <div className="px-5 pt-4 pb-2 text-[13px] font-semibold" style={{ color: '#030524' }}>{title}</div>
      <div className="divide-y divide-slate-100">{children}</div>
    </div>
  );
}

function Row({ label, value, testId }) {
  return (
    <div className="flex items-center justify-between gap-4 px-5 py-2.5 text-[13px]" data-testid={testId}>
      <span className="text-slate-500">{label}</span>
      <span className="font-semibold text-right" style={{ color: '#030524' }}>{value}</span>
    </div>
  );
}

const Empty = ({ children }) => <div className="px-5 py-3 text-[12px] text-slate-400 italic">{children}</div>;

const money = (n) => (present(n) ? formatMoney(n, true) : '—');

function StatusPanel({ arive }) {
  const status = arive.status || {};
  const history = arive.stage_history || [];
  const keyDates = Object.entries(arive.key_dates || {}).filter(([, v]) => present(v));
  return (
    <Panel title="Arive status & key dates" testId="arive-status-panel">
      <Row label="Stage" value={status.stage || '—'} testId="arive-stage" />
      {present(status.adverse_reason) && <Row label="Adverse reason" value={status.adverse_reason} testId="arive-adverse" />}
      {present(status.at) && <Row label="Stage changed" value={formatDateTime(status.at)} />}
      {history.length > 0 && (
        <div className="px-5 py-2.5" data-testid="arive-stage-history">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 mb-1">Stage history</div>
          {history.map((h, i) => (
            <div key={`${h.stage}-${i}`} className="flex justify-between text-[12px] py-0.5">
              <span className="text-slate-600">{h.stage}</span>
              <span className="text-slate-400">{h.at ? formatDateTime(h.at) : '—'}</span>
            </div>
          ))}
        </div>
      )}
      {keyDates.map(([k, v]) => <Row key={k} label={titleCase(k)} value={formatDate(v)} testId={`arive-date-${k}`} />)}
      {keyDates.length === 0 && <Empty>No key dates yet.</Empty>}
    </Panel>
  );
}

function TrackersPanel({ arive }) {
  const trackers = arive.trackers || [];
  return (
    <Panel title="Trackers" testId="arive-trackers-panel">
      {trackers.map((t) => (
        <Row
          key={t.name}
          label={t.label || titleCase(t.name)}
          value={[t.status, t.date ? formatDate(t.date) : null].filter(Boolean).join(' · ') || '—'}
          testId={`arive-tracker-${t.name}`}
        />
      ))}
      {trackers.length === 0 && <Empty>No trackers.</Empty>}
    </Panel>
  );
}

function PricingPanel({ arive }) {
  const p = arive.pricing || {};
  const rows = [
    ['Note rate', present(p.note_rate) ? `${p.note_rate}%` : null],
    ['Discount points', p.discount_points],
    ['Lock status', p.lock_status],
    ['Lock date', p.lock_date ? formatDate(p.lock_date) : null],
    ['Lock expires', p.lock_expiration_date ? formatDate(p.lock_expiration_date) : null],
    ['Lender', [p.lender_name, p.lender_nmls && `NMLS ${p.lender_nmls}`].filter(Boolean).join(' · ') || null],
    ['Product', [p.lender_product_name, p.lender_product_code].filter(Boolean).join(' · ') || null],
    ['Lender loan #', p.lender_loan_identifier],
  ].filter(([, v]) => present(v));
  return (
    <Panel title="Pricing & lock" testId="arive-pricing-panel">
      {rows.map(([label, value]) => <Row key={label} label={label} value={String(value)} />)}
      {rows.length === 0 && <Empty>No pricing yet.</Empty>}
    </Panel>
  );
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Arive sends month + day only, never a birth year.
const birthday = (b) => (MONTHS[b.birth_month - 1] && b.birth_day ? `${MONTHS[b.birth_month - 1]} ${b.birth_day}` : null);

function BorrowersPanel({ arive }) {
  const borrowers = arive.borrowers || [];
  return (
    <Panel title="Borrowers" testId="arive-borrowers-panel">
      {borrowers.map((b, i) => {
        const name = [b.first_name, b.last_name].filter(Boolean).join(' ') || b.email || 'Unnamed';
        const bday = birthday(b);
        return (
          <div key={`${b.email || name}-${i}`} className="flex items-center justify-between gap-4 px-5 py-2.5 text-[13px]" data-testid={`arive-borrower-${i}`}>
            <div className="min-w-0">
              <div className="font-semibold truncate" style={{ color: '#030524' }}>{name}</div>
              <div className="text-[11px] text-slate-400 truncate">{b.applicant_type || '—'}</div>
            </div>
            <span className="text-[12px] text-slate-500 whitespace-nowrap" data-testid={`arive-borrower-birthday-${i}`}>
              {bday ? `Birthday ${bday}` : '—'}
            </span>
          </div>
        );
      })}
      {borrowers.length === 0 && <Empty>No borrowers.</Empty>}
    </Panel>
  );
}

function TeamPanel({ arive }) {
  const team = arive.team || [];
  return (
    <Panel title="Loan team" testId="arive-team-panel">
      {team.map((m, i) => {
        const name = [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email || 'Unnamed';
        const role = [m.role, m.primary ? 'primary' : null].filter(Boolean).join(' · ');
        return (
          <div key={`${m.email || name}-${i}`} className="flex items-center justify-between gap-4 px-5 py-2.5 text-[13px]" data-testid={`arive-team-${i}`}>
            <div className="min-w-0">
              <div className="font-semibold truncate" style={{ color: '#030524' }}>{name}</div>
              <div className="text-[11px] text-slate-400 truncate">{[m.email, m.phone].filter(Boolean).join(' · ')}</div>
            </div>
            <span className="text-[12px] text-slate-500 whitespace-nowrap">{role || '—'}</span>
          </div>
        );
      })}
      {team.length === 0 && <Empty>No team members.</Empty>}
    </Panel>
  );
}

function MoneyPanel({ arive }) {
  const comp = arive.money_compensation;
  const margin = arive.money_margin;
  return (
    <Panel title="Company money" testId="arive-money-panel">
      {has(arive, 'money_compensation') && (
        <>
          <Row label="Compensation" value={money(comp?.compensation)} testId="arive-money-compensation" />
          <Row label="Compensation type" value={comp?.compensation_type || '—'} />
        </>
      )}
      {has(arive, 'money_margin') && (
        <>
          <Row label="Gross revenue" value={money(margin?.gross_revenue)} testId="arive-money-gross" />
          <Row label="Net revenue" value={money(margin?.net_revenue)} />
          <Row label="Broker fee" value={money(margin?.broker_fee)} />
          <Row label="Reimbursements" value={money(margin?.reimbursements)} />
          <Row label="Tolerance cures" value={money(margin?.tolerance_cures)} />
          <Row label="Financed fees" value={money(margin?.financed_fees)} />
        </>
      )}
    </Panel>
  );
}

export function AriveModule({ loan }) {
  const arive = loan.arive;
  if (!arive) return null;
  const showMoney = has(arive, 'money_compensation') || has(arive, 'money_margin');
  return (
    <div className="space-y-4" data-testid="arive-module">
      <ModuleHeader title="Arive" subtitle="Synced from Arive. Edit these in Arive; they are read-only here." />
      <div className="flex flex-wrap items-center gap-2">
        <AriveBadge deepLink={ariveDeepLink(loan)} />
        <AriveChips arive={arive} />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="space-y-4">
          <StatusPanel arive={arive} />
          <TrackersPanel arive={arive} />
        </div>
        <div className="space-y-4">
          <PricingPanel arive={arive} />
          <BorrowersPanel arive={arive} />
          <TeamPanel arive={arive} />
          {showMoney && <MoneyPanel arive={arive} />}
        </div>
      </div>
    </div>
  );
}
